import { Link } from "@tanstack/react-router";
import { serializeCompareParam } from "../lib/compare";
import { unitsById } from "../lib/dataset";
import { type StatFrame, serializeFrames } from "../lib/frame";
import { ArtImage } from "./art-image";

/** The comparison set's fixed-tray affordance (design language: "fixed bottom tray with
 * inverted Compare N button") — rendered as the LAST element of a page's content, sticky
 * to the viewport bottom while there is page left to scroll and settling in flow at the
 * end, so it never occludes the footer and needs no spacer math. Ids the dataset does not
 * know (a pasted link from another patch) still chip by raw id so they stay removable. */
export function CompareTray({
  compare,
  frame,
  faction,
  onChange,
}: {
  compare: readonly string[];
  frame: StatFrame;
  /** The Faction Selection's search params — it filters nothing on /compare (ADR 0010) but
   * rides in so navigation keeps the reader's empire. */
  faction: { faction?: string };
  onChange: (next: string[]) => void;
}) {
  if (compare.length === 0) return null;

  // A removal unmounts the clicked × (and Clear unmounts the whole tray); without a new
  // home focus falls to <body> and a keyboard user loses their place. The next remaining
  // chip's ×, or the main landmark when the tray goes, is the nearest equivalent
  // (review-round finding).
  function changeWithFocus(next: string[]) {
    onChange(next);
    requestAnimationFrame(() => {
      const chip = document.querySelector<HTMLButtonElement>("button[data-tray-remove]");
      if (next.length > 0 && chip) chip.focus();
      else document.querySelector<HTMLElement>("main")?.focus();
    });
  }

  return (
    <div className="sticky bottom-0 z-30 -mx-4 border-t border-line-strong bg-surface-deep px-4 py-2.5 sm:-mx-6 sm:px-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="hidden font-mono text-[10px] uppercase tracking-[0.12em] text-muted sm:inline">
          comparison set
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {compare.map((id) => {
            const unit = unitsById.get(id);
            return (
              <span
                key={id}
                className="flex items-center gap-2 rounded border border-line bg-surface px-2 py-1"
              >
                {unit && (
                  <span className="flex h-4 w-9 shrink-0 items-center justify-center">
                    <ArtImage
                      asset={unit.art.hudIcon}
                      alt=""
                      className="max-h-4 max-w-9 object-contain"
                    />
                  </span>
                )}
                <span className="max-w-[140px] truncate text-[12px] text-ink-2 sm:max-w-none">
                  {unit ? unit.name : id}
                </span>
                <button
                  type="button"
                  data-tray-remove
                  aria-label={`Remove ${unit ? unit.name : id} from comparison`}
                  onClick={() => changeWithFocus(compare.filter((existing) => existing !== id))}
                  className="cursor-pointer text-[13px] leading-none text-muted hover:text-vasari"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
        <Link
          to="/compare"
          // The comparison carries one frame slot PER COLUMN (ADR 0011), so the page's own
          // frame goes to every column the tray is handing over — the same stats the reader
          // was just looking at, on all of them, rather than only on the first.
          search={{
            units: serializeCompareParam(compare),
            ...serializeFrames(compare.map(() => frame)),
            ...faction,
          }}
          className="rounded bg-ink px-3.5 py-1.5 text-[12.5px] font-semibold text-bg hover:bg-ink-2"
        >
          Compare {compare.length}
        </Link>
        <button
          type="button"
          onClick={() => changeWithFocus([])}
          className="cursor-pointer text-[12px] text-muted hover:text-ink-2"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
